/**
 * JSON Subtitle Parser
 * Parses JSON transcript files (e.g., from Descript) and strips stage directions
 */

export interface SubtitleCue {
  index: number;
  startTime: number; // seconds
  endTime: number;   // seconds
  text: string;
}

/**
 * Strip stage directions from subtitle text
 * Removes content within square brackets like [chuckle], [grins], etc.
 * Preserves punctuation for proper sentence formatting
 */
function stripStageDirections(text: string): string {
  return text
    .replace(/\[.*?\]/g, '')  // Remove bracketed content like [laughs]
    .replace(/\s+/g, ' ')     // Collapse multiple spaces
    .trim();
}

/**
 * Parse JSON transcript content into an array of SubtitleCues
 * Supports various JSON formats from transcription tools
 * Prioritizes word-level timestamps when available
 */
export function parseJSONSubtitles(jsonContent: string): SubtitleCue[] {
  const cues: SubtitleCue[] = [];
  
  try {
    const data = JSON.parse(jsonContent);
    
    // Handle different JSON transcript formats
    
    // Format 1: Array of segments with start, end, text
    if (Array.isArray(data)) {
      return parseSegmentsOrWords(data);
    }
    
    // Format 2: Object with segments array (e.g., Descript format)
    const segments = data.segments || data.words || data.cues || data.transcript || [];
    
    if (Array.isArray(segments)) {
      // Check if segments have word-level timing
      for (const segment of segments) {
        if (segment.words && Array.isArray(segment.words)) {
          // Use word-level timestamps for precise sync
          const wordCues = parseWords(segment.words);
          cues.push(...wordCues);
        } else {
          // Fall back to segment-level timing
          const text = stripStageDirections(segment.text || segment.word || segment.content || '');
          if (text) {
            cues.push({
              index: cues.length + 1,
              startTime: segment.start || segment.startTime || segment.start_time || 0,
              endTime: segment.end || segment.endTime || segment.end_time || 0,
              text
            });
          }
        }
      }
    }
    
  } catch (error) {
    console.error('Error parsing JSON subtitles:', error);
  }
  
  // Re-index all cues sequentially
  return cues.map((cue, idx) => ({ ...cue, index: idx + 1 }));
}

/**
 * Parse an array of word objects into SubtitleCues
 * Skips spaces and stage directions
 */
function parseWords(words: any[]): SubtitleCue[] {
  const cues: SubtitleCue[] = [];
  
  for (const word of words) {
    const rawText = word.text || word.word || '';
    
    // Skip empty strings and whitespace-only entries
    if (!rawText || rawText.trim() === '') {
      continue;
    }
    
    // Strip stage directions and check if anything remains
    const text = stripStageDirections(rawText);
    if (!text) {
      continue;
    }
    
    cues.push({
      index: cues.length + 1,
      startTime: word.start || word.startTime || word.start_time || 0,
      endTime: word.end || word.endTime || word.end_time || 0,
      text
    });
  }
  
  // Fill timing gaps: extend each word's end time to the next word's start time
  // This prevents "missing" words when currentTime falls between words
  for (let i = 0; i < cues.length - 1; i++) {
    const currentCue = cues[i];
    const nextCue = cues[i + 1];
    
    // If there's a gap, extend current word to fill it
    if (currentCue.endTime < nextCue.startTime) {
      currentCue.endTime = nextCue.startTime;
    }
  }
  
  return cues;
}

/**
 * Parse a simple array of segments or words
 */
function parseSegmentsOrWords(items: any[]): SubtitleCue[] {
  const cues: SubtitleCue[] = [];
  
  for (const item of items) {
    // Check if this item has nested words
    if (item.words && Array.isArray(item.words)) {
      const wordCues = parseWords(item.words);
      cues.push(...wordCues);
    } else {
      const rawText = item.text || item.word || item.content || '';
      if (!rawText || rawText.trim() === '') continue;
      
      const text = stripStageDirections(rawText);
      if (!text) continue;
      
      cues.push({
        index: cues.length + 1,
        startTime: item.start || item.startTime || item.start_time || 0,
        endTime: item.end || item.endTime || item.end_time || 0,
        text
      });
    }
  }
  
  return cues.map((cue, idx) => ({ ...cue, index: idx + 1 }));
}

/**
 * Find the active cue for a given playback time
 */
export function findActiveCue(cues: SubtitleCue[], currentTime: number): SubtitleCue | null {
  for (const cue of cues) {
    if (currentTime >= cue.startTime && currentTime <= cue.endTime) {
      return cue;
    }
  }
  return null;
}
